import React, { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import SidebarPrototypePage from './pages/SidebarPrototypePage';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { MutationQueueProvider } from './providers/MutationQueueProvider';
import { useCurrentUser } from './providers/UserProvider';
import { ModalProvider } from './providers/ModalProvider';
import { WorkspaceProvider } from './providers/WorkspaceProvider';
import NoAccess from './pages/NoAccess';
import ConfirmationModalProvider from './providers/ConfirmationModalProvider';
import { WorkspacePermissionsProvider } from './providers/WorkspacePermissionsProvider';
import { TemplateProvider } from './providers/TemplateProvider';
import { URLParamProvider } from './providers/URLParamProvider';
import { LabelProvider } from './providers/LabelProvider';
import { WatcherProvider } from './providers/WatcherProvider';
import { MyInspectionsProvider } from './providers/MyInspectionsProvider';
import { RecordingProvider } from './providers/VoiceRecorderProvider';

export function WebApp() {
  const ldClient = useLDClient();
  const { user } = useCurrentUser();
  const identifiedUserIdRef = useRef(null);

  useEffect(() => {
    if (user && ldClient && identifiedUserIdRef.current !== user.id) {
      identifiedUserIdRef.current = user.id;
      ldClient
        .identify({
          key: user.id,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          custom: {
            'company-id': user.company?.id,
            'company-name': user.company?.name,
            role: user.role,
          },
        })
        .catch((err) => {
          // Don't clear identifiedUserIdRef synchronously here - on flaky
          // connections `user` can churn (new object, same id) rapidly via
          // UserProvider refetches, and an immediate reset would let each
          // churn re-trigger identify() again, hitting the same failure in
          // a tight loop. Back off before allowing a retry instead.
          console.error('LaunchDarkly identify failed', err);
          setTimeout(() => {
            if (identifiedUserIdRef.current === user.id) {
              identifiedUserIdRef.current = null;
            }
          }, 30000);
        });
    }
  }, [ldClient, user]);

  return (
    <>
      <WorkspaceProvider>
        <WorkspacePermissionsProvider>
          <URLParamProvider>
            <RecordingProvider>
              <TemplateProvider>
                <MyInspectionsProvider>
                  <LabelProvider>
                    <WatcherProvider>
                      <ModalProvider>
                        <ConfirmationModalProvider>
                          <NoAccess>
                            <MutationQueueProvider user={user}>
                              <SidebarPrototypePage>
                                <Outlet />
                              </SidebarPrototypePage>
                            </MutationQueueProvider>
                          </NoAccess>
                        </ConfirmationModalProvider>
                      </ModalProvider>
                    </WatcherProvider>
                  </LabelProvider>
                </MyInspectionsProvider>
              </TemplateProvider>
            </RecordingProvider>
          </URLParamProvider>
        </WorkspacePermissionsProvider>
      </WorkspaceProvider>
    </>
  );
}

export default WebApp;
